/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { workloadIdentitiesRegisterIssuer } from "../funcs/workloadIdentitiesRegisterIssuer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { WorkloadIdentityPolicy } from "../models/components/workloadidentitypolicy.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RegisterWorkloadIssuerRequest,
  RegisterWorkloadIssuerSecurity,
} from "../models/operations/registerworkloadissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RegisterWorkloadIssuerMutationVariables = {
  request: RegisterWorkloadIssuerRequest;
  security?: RegisterWorkloadIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type RegisterWorkloadIssuerMutationData = WorkloadIdentityPolicy;

export type RegisterWorkloadIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * registerIssuer workloadIdentities
 *
 * @remarks
 * Trust an external issuer to vouch for workloads. Requires workload:write. Returns the whole policy, so a caller replaces its view rather than merging into it.
 */
export function useRegisterWorkloadIssuerMutation(
  options?: MutationHookOptions<
    RegisterWorkloadIssuerMutationData,
    RegisterWorkloadIssuerMutationError,
    RegisterWorkloadIssuerMutationVariables
  >,
): UseMutationResult<
  RegisterWorkloadIssuerMutationData,
  RegisterWorkloadIssuerMutationError,
  RegisterWorkloadIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRegisterWorkloadIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyRegisterWorkloadIssuer(): MutationKey {
  return ["@gram/client", "workloadIdentities", "registerIssuer"];
}

export function buildRegisterWorkloadIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RegisterWorkloadIssuerMutationVariables,
  ) => Promise<RegisterWorkloadIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyRegisterWorkloadIssuer(),
    mutationFn: function registerWorkloadIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<RegisterWorkloadIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(workloadIdentitiesRegisterIssuer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
